import { DataTable, ErrorState, type LocalDataColumn, Skeleton } from "@nymbl/nos2-rules";
import { useMemo } from "react";
import { usePersonMonths } from "@/hooks/use-team";
import { PERSON_MONTHS_HELP } from "@/lib/column-help";
import { formatHours, type PersonMetrics, personMetrics } from "@/lib/team/metrics";
import { monthParam } from "@/lib/team/months";
import type { IsoDate } from "@/lib/time/dates";
import { cn } from "@/lib/utils";

const MONTH_SHORT = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short" });
const MONTH_YEAR = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  year: "numeric",
});
const MONEY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const utc = (d: IsoDate) => new Date(`${d}T00:00:00Z`);

/** Logged-group numbers are blank (shown "—") before the month starts. */
const LOGGED = new Set(["billable", "logged", "billablePct", "utilization"]);
type NumberKey =
  | "available"
  | "targetPct"
  | "planned"
  | "billable"
  | "logged"
  | "billablePct"
  | "utilization";

const showPct = (v: number) => `${Math.round(v)}%`;

function numberValue(key: NumberKey, kind: "hours" | "pct", m: PersonMetrics): number | null {
  if (LOGGED.has(key) && !m.started) return null;
  const v = m[key];
  if (v === null) return null;
  return kind === "pct" ? v * 100 : v;
}

function numberColumn(
  key: NumberKey,
  label: string,
  kind: "hours" | "pct",
): LocalDataColumn<PersonMetrics> {
  return {
    key,
    label,
    type: "number",
    accessor: (m) => numberValue(key, kind, m),
    render: (m) => {
      const v = numberValue(key, kind, m);
      const text = v === null ? "—" : kind === "pct" ? showPct(v) : formatHours(v);
      const red = key === "billablePct" && m.belowTarget;
      return (
        <span
          className={cn(!m.started && "text-muted-foreground", red && "font-semibold text-danger")}
        >
          {text}
        </span>
      );
    },
    help: PERSON_MONTHS_HELP[key],
  };
}

const monthName = (d: string) => MONTH_YEAR.format(utc(d as IsoDate));

export const MONTHS_COLUMNS: LocalDataColumn<PersonMetrics>[] = [
  {
    key: "month",
    label: "Month",
    type: "date",
    accessor: (m) => m.monthStart,
    render: (m) => <span className="font-semibold">{monthName(m.monthStart)}</span>,
    searchText: (m) => monthName(m.monthStart),
    help: PERSON_MONTHS_HELP.month,
  },
  numberColumn("available", "Available", "hours"),
  numberColumn("targetPct", "Target", "pct"),
  numberColumn("planned", "Guidance", "hours"),
  numberColumn("billable", "Billable", "hours"),
  numberColumn("logged", "Total", "hours"),
  numberColumn("billablePct", "Billable %", "pct"),
  numberColumn("utilization", "Utilization", "pct"),
  {
    key: "amount",
    label: "Billable $",
    type: "number",
    accessor: (m) => (m.started && m.billableAmount > 0 ? m.billableAmount : null),
    render: (m) => (m.started && m.billableAmount > 0 ? MONEY.format(m.billableAmount) : "—"),
    help: PERSON_MONTHS_HELP.amount,
  },
];

export function PersonSummaryTab({ personId, month }: { personId: string; month: IsoDate }) {
  const query = usePersonMonths(personId, monthParam(month));
  const months = useMemo(
    () => (query.data ? query.data.months.map((r) => personMetrics(r, query.data.asOf)) : []),
    [query.data],
  );
  const max = Math.max(
    1,
    ...months.map((m) => Math.max(m.available ?? 0, m.started ? m.logged : 0)),
  );

  if (query.isError)
    return <ErrorState message="Didn't load" onRetry={() => void query.refetch()} />;
  if (query.isPending) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-6">
      <section aria-labelledby="trend-h" className="space-y-2">
        <h3 id="trend-h" className="font-semibold">
          Logged vs available
        </h3>
        <div className="flex h-44 items-end gap-3 overflow-x-auto">
          {months.map((m) => {
            const logged = m.started ? m.logged : 0;
            const billable = m.started ? m.billable : 0;
            const h = (v: number) => `${(v / max) * 100}%`;
            return (
              <div
                key={m.monthStart}
                role="img"
                aria-label={`${MONTH_SHORT.format(utc(m.monthStart))}: ${formatHours(logged)} logged, ${formatHours(billable)} billable, ${formatHours(m.available)} available`}
                className="flex h-full min-w-10 flex-1 flex-col items-center justify-end gap-1"
              >
                <span className="text-xs tabular-nums">
                  {m.started ? formatHours(logged) : "—"}
                </span>
                <div className="relative flex w-full flex-1 items-end justify-center">
                  <div
                    className="absolute bottom-0 w-full rounded-t border border-dashed border-muted-foreground/60"
                    style={{ height: h(m.available ?? 0) }}
                  />
                  <div className="flex h-full w-3/5 flex-col justify-end">
                    <div
                      className="bg-primary/40"
                      style={{ height: h(Math.max(0, logged - billable)) }}
                    />
                    <div className="rounded-b bg-primary" style={{ height: h(billable) }} />
                  </div>
                </div>
                <span className={cn("text-xs", m.monthStart === month && "font-bold")}>
                  {MONTH_SHORT.format(utc(m.monthStart))}
                </span>
              </div>
            );
          })}
        </div>
      </section>
      <section aria-label="Months table">
        <DataTable
          label="Months"
          search={{ placeholder: "Search months…" }}
          exportName="person-months"
          columns={MONTHS_COLUMNS}
          rows={months}
          rowKey={(m) => m.monthStart}
          defaultSort="month"
          defaultSortDir="desc"
          rowProps={(m) => ({ className: m.monthStart === month ? "bg-accent/40" : undefined })}
        />
      </section>
    </div>
  );
}
